import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { faExclamationTriangle } from '@fortawesome/pro-light-svg-icons';
import styled from 'styled-components';
import { BatchReturnStatusType } from 'returnCenter-api';
import useAppDispatch from 'hooks/useAppDispatch';
import useAppSelector from 'hooks/useAppSelector';
import useBreakPoints from 'hooks/useBreakPoints';
import {
    fetchBatchReturnById,
    updateBatchReturnStatus,
    receiveBatchReturn,
    fetchProcessingSummary,
    resetCurrentBatchReturn,
    selectCurrentBatchReturn,
    selectCurrentBatchReturnLoading,
    selectCurrentBatchReturnError,
    selectCurrentBatchReturnOrderGroups,
    selectIsReturnStatusCompleted,
} from 'store/reducers/batchReturnSlice';
import { companiesSelector } from 'store/reducers/companiesSlice';
import Flex from 'components/ui/Flex';
import Grid from 'components/ui/Grid';
import Spinner from 'components/ui/Spinner';
import ErrorDisplay from 'components/Error/ErrorDisplay';
import ConfirmationDialog from 'components/ui/ConfirmationDialog';
import SideBar from 'components/SideBar/SideBar';
import Table from 'components/ui/Table';
import BatchReturnHeadDetails from './BatchReturnHeadDetails';
import BatchReturnActionPanel from './BatchReturnActionPanel';
import BatchReturnProcessingPanel from './BatchReturnProcessingPanel';
import useBatchReturnTableRows from 'hooks/useBatchReturnTableRows';
import { toast } from 'react-toastify';
import { BATCH_BP } from './BatchReturn';
import OrderItemCountDisplay from 'components/Orders/OrderItemCountDisplay';
import Text from 'components/ui/Text';
import { getBatchStatusTypeString } from 'utils/helpers';
import { setSelectedBatchStatus } from 'store/reducers/navigationSlice';

type ActiveDialog = 'close' | 'sent' | 'received' | null;

const BatchReturnDetail = () => {
    const { t } = useTranslation();
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const bp = useBreakPoints();

    const batchReturn = useAppSelector(selectCurrentBatchReturn);
    const isReturnCompleted = useAppSelector(selectIsReturnStatusCompleted);
    const loading = useAppSelector(selectCurrentBatchReturnLoading);
    const error = useAppSelector(selectCurrentBatchReturnError);
    const selectedCompany = useAppSelector(companiesSelector.selectedCompany);
    const selectedMarketId = useAppSelector(companiesSelector.selectSelectedMarketId);
    const orderGroups = useAppSelector(selectCurrentBatchReturnOrderGroups);

    const [activeDialog, setActiveDialog] = useState<ActiveDialog>(null);
    const [isUpdating, setIsUpdating] = useState(false);

    const compact = bp.width < 900;

    const { headers, getRows } = useBatchReturnTableRows({
        origin: 'batch',
    });

    useEffect(() => {
        if (id) {
            dispatch(fetchBatchReturnById(Number(id)));
        }
        return () => {
            dispatch(resetCurrentBatchReturn());
        };
    }, [dispatch, id]);

    // Fetch processing summary when status >= Received
    useEffect(() => {
        if (batchReturn?.id && batchReturn.status != null && batchReturn.status >= BatchReturnStatusType.Received) {
            dispatch(fetchProcessingSummary(batchReturn.id));
        }
    }, [dispatch, batchReturn?.id, batchReturn?.status]);

    const handleCloseBatch = useCallback(() => {
        setActiveDialog('close');
    }, []);

    const handleMarkAsSent = useCallback(() => {
        setActiveDialog('sent');
    }, []);

    const handleMarkAsReceived = useCallback(() => {
        setActiveDialog('received');
    }, []);

    const executeBatchAction = useCallback(
        async (action: () => Promise<void>) => {
            const batchReturnId = batchReturn?.id;

            if (!batchReturnId) return;

            setIsUpdating(true);

            try {
                await action();

                const updatedBatchReturn = await dispatch(fetchBatchReturnById(batchReturnId)).unwrap();
                const statusType = updatedBatchReturn.status ?? null;
                const statusKey = getBatchStatusTypeString(updatedBatchReturn.status);

                if (statusKey) {
                    dispatch(setSelectedBatchStatus(statusType));
                    navigate(`/batch-returns/${statusKey}/${batchReturnId}`);
                }
            } catch (error) {
                toast.error(
                    t('batchReturn.actionFailed', 'Action failed. Please try again.', {
                        error: error?.toString() ?? '',
                    }),
                    {
                        autoClose: 3500,
                        closeOnClick: true,
                    },
                );
            } finally {
                setIsUpdating(false);
                setActiveDialog(null);
            }
        },
        [dispatch, batchReturn?.id],
    );

    const handleConfirmClose = useCallback(async () => {
        await executeBatchAction(async () => {
            if (!batchReturn?.id) return;
            await dispatch(
                updateBatchReturnStatus({
                    id: batchReturn.id,
                    status: BatchReturnStatusType.Closed,
                }),
            ).unwrap();
        });
    }, [dispatch, batchReturn?.id, executeBatchAction]);

    const handleConfirmSent = useCallback(async () => {
        await executeBatchAction(async () => {
            if (!batchReturn?.id) return;
            await dispatch(
                updateBatchReturnStatus({
                    id: batchReturn.id,
                    status: BatchReturnStatusType.Sent,
                }),
            ).unwrap();
        });
    }, [dispatch, batchReturn?.id, executeBatchAction]);

    const handleConfirmReceived = useCallback(async () => {
        await executeBatchAction(async () => {
            if (!batchReturn?.id) return;
            await dispatch(receiveBatchReturn(batchReturn.id)).unwrap();
        });
    }, [dispatch, batchReturn?.id, executeBatchAction]);

    const handleBack = useCallback(() => {
        navigate(-1);
    }, [navigate]);

    if (error) {
        return (
            <Flex className={`${bp.width < BATCH_BP ? 'pt-3' : 'p-3'}`} gap="1rem" column $grow={1}>
                <ErrorDisplay
                    errorMessage={error}
                    title={t('error_occurred')}
                    icon={faExclamationTriangle}
                    onBack={handleBack}
                />
            </Flex>
        );
    }

    if (loading || !batchReturn) {
        return (
            <Flex
                className={`${bp.width < BATCH_BP ? 'pt-3' : 'p-3'}`}
                gap="1rem"
                column
                justify="center"
                align="center"
                $grow={1}
            >
                <SpinnerWrapper justify="center" align="center" $grow={1}>
                    <Spinner useFullHeight color="text" size="small" />
                </SpinnerWrapper>
            </Flex>
        );
    }

    const isReceivedForLayout = batchReturn.status != null && batchReturn.status >= BatchReturnStatusType.Received;

    return (
        <Flex className={`batch-return-detail ${bp.width < BATCH_BP ? 'p-3' : ''}`} gap="1rem" column $grow={1}>
            <GridWrapper columns={{ base: '1fr', md: '2fr 0.55fr', lg: '2fr 0.55fr' }} gap="1rem">
                <Flex column $grow={1}>
                    <BatchReturnHeadDetails batchReturn={batchReturn} />
                    <TableContainer column $grow={1}>
                        {orderGroups.length === 0 ? (
                            <Padding>
                                <Text as="p" fontSize={16} className="text-center pl-3">
                                    {t('batchReturns.noItems', 'No items found for this batch return.')}
                                </Text>
                            </Padding>
                        ) : (
                            orderGroups.map(([orderNumber, orderGroup]) => {
                                const orderLines = orderGroup.orderLines ?? [];
                                const rows = getRows(orderNumber, orderLines);

                                return (
                                    <Flex column $grow={1} key={orderNumber}>
                                        <OrderItemCountDisplay
                                            title={`${t('order', 'Order')}: ${orderNumber}`}
                                            countLabel={t('items', 'items')}
                                            items={orderLines}
                                            showActions={false}
                                        />

                                        <Padding>
                                            {rows.length > 0 ? (
                                                <Table
                                                    className={`${orderNumber}-order-table f3-300`}
                                                    compact={compact}
                                                    headers={headers}
                                                    cols={headers.length}
                                                    rows={rows}
                                                    bgColor="fillWhite"
                                                    colLayout="max-content minmax(180px, 2fr) repeat(5, minmax(90px, 1fr))"
                                                />
                                            ) : (
                                                <Text as="p" fontSize={16} className="text-center pl-3">
                                                    {t('batchReturns.noItems', 'No items found for this order.')}
                                                </Text>
                                            )}
                                        </Padding>
                                    </Flex>
                                );
                            })
                        )}
                    </TableContainer>
                </Flex>
                <Flex className="right-column-section" column gap="1rem">
                    <SideBar>
                        <BatchReturnActionPanel
                            status={batchReturn.status}
                            isSender={
                                (batchReturn.contextId ?? undefined) === selectedCompany &&
                                (batchReturn.marketId ?? undefined) === selectedMarketId
                            }
                            onClose={handleCloseBatch}
                            onMarkAsSent={handleMarkAsSent}
                            onMarkAsReceived={handleMarkAsReceived}
                            isLoading={isUpdating}
                            returnStatus={isReturnCompleted}
                        />

                        {isReceivedForLayout && batchReturn.id && (
                            <BatchReturnProcessingPanel batchReturnId={batchReturn.id} />
                        )}

                        <ConfirmationDialog
                            open={activeDialog === 'close'}
                            onClose={() => setActiveDialog(null)}
                            title={t('batchReturn.confirmClose', 'Close batch return')}
                            content={t(
                                'batchReturn.confirmCloseMessage',
                                'Are you sure you want to close this batch return? No more items can be added after closing.',
                            )}
                            onCancel={() => setActiveDialog(null)}
                            onConfirm={handleConfirmClose}
                        />

                        <ConfirmationDialog
                            open={activeDialog === 'sent'}
                            onClose={() => setActiveDialog(null)}
                            title={t('batchReturn.confirmSent', 'Mark as sent')}
                            content={t(
                                'batchReturn.confirmSentMessage',
                                'Are you sure you want to mark this batch return as sent?',
                            )}
                            onCancel={() => setActiveDialog(null)}
                            onConfirm={handleConfirmSent}
                        />

                        <ConfirmationDialog
                            open={activeDialog === 'received'}
                            onClose={() => setActiveDialog(null)}
                            title={t('batchReturn.confirmReceived', 'Mark as received')}
                            content={t(
                                'batchReturn.confirmReceivedMessage',
                                'Are you sure you want to mark this batch return as received? This will start the handling process.',
                            )}
                            onCancel={() => setActiveDialog(null)}
                            onConfirm={handleConfirmReceived}
                        />
                    </SideBar>
                </Flex>
            </GridWrapper>
        </Flex>
    );
};

export default BatchReturnDetail;

const GridWrapper = styled(Grid)`
    margin-top: 3rem;
`;

const SpinnerWrapper = styled(Flex)`
    margin-top: 3rem;
`;

const TableContainer = styled(Flex)`
    margin-top: 1rem;
`;

const Padding = styled(Flex)`
    border-radius: var(--radius);
    padding: 0.5rem 0;
    flex: 1;
    width: 100%;
    background-color: ${({ theme }) => theme.colors.fillWhite};
`;
